import * as React from 'react';
import { AlertTriangle, List, Search } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  DetailEmpty,
  ListDetailLayout,
  ListGroupLabel,
  ListItem,
  ListPane,
  SearchField,
  WorkspaceButton,
} from '@/components/ui/workspace';
import { useCoverageQuery, useDiscoverySummaryQuery } from '@/lib/api/discovery';
import { useUntaggedPainPointsQuery } from '@/lib/api/discovery-findings';
import { CoverageDot, ReviewCount } from '../coverage-bits';
import { plural, tabCounts } from '../discovery-logic';
import { AreaFindingsView, parseAreaView } from './area-findings';
import { EvidenceLedger } from './evidence-ledger';
import { UNTAGGED_AREA, areaStatusLine, defaultAreaId, parseLedgerStatus } from './findings-logic';
import { UntaggedPainPoints } from './pain-points';

/**
 * Discovery -> Findings (T-0188, E12-F06 / E12-F07). One screen, read one
 * way: the areas on the left, everything about the selected area on the
 * right, top to bottom; "All evidence" opens the ledger in the same
 * place. Prototype: `findingsView` in `docs/discovery/prototype/discovery.js`
 * (`#findings/D5`, `#findings/all`).
 *
 * The address holds the state so a view can be shared:
 *   ?area=<area id | all | untagged>&status=<ledger status>&q=<search>&evidence=<id>
 *   &document=<id>   a document whose proposed passages are open for review (T-0257)
 *   &view=<process | pain-points | quotes>   how the area is read (5 Nov 2026)
 * Typing in the search opens the ledger, as in the prototype.
 *
 * "Not tagged to an area" appears while a pain point that is not dismissed
 * has no active area: no area page lists it, yet the sign-off checklist
 * counts it while it is proposed, so it is tagged, confirmed or dismissed
 * there.
 */

export interface FindingsSearch {
  area?: string;
  status?: string;
  q?: string;
  evidence?: string;
  document?: string;
  view?: string;
}

export function FindingsScreen({
  engagementId,
  search,
  onSearchChange,
}: {
  engagementId: string;
  search: FindingsSearch;
  onSearchChange: (next: FindingsSearch, opts?: { replace?: boolean }) => void;
}) {
  const coverage = useCoverageQuery(engagementId);
  const summary = useDiscoverySummaryQuery(engagementId);
  const untagged = useUntaggedPainPointsQuery(engagementId);
  const areas = coverage.data?.areas ?? [];
  const loose = (untagged.data ?? []).filter((p) => p.status !== 'dismissed');
  const looseProposed = loose.filter((p) => p.status === 'proposed').length;
  const known = (id: string | undefined) => Boolean(id && areas.some((a) => a.area_id === id));
  const selected =
    search.area === 'all' || (!search.area && (search.status || search.q))
      ? 'all'
      : search.area === UNTAGGED_AREA
        ? UNTAGGED_AREA
        : known(search.area)
          ? search.area!
          : defaultAreaId(areas);
  const q = search.q ?? '';

  const select = (area: string) =>
    onSearchChange({ area, q: area === 'all' ? search.q : undefined });

  const list = (
    <ListPane
      label="Areas"
      head={
        <SearchField
          value={q}
          onChange={(next) =>
            onSearchChange(
              { area: 'all', status: search.status, q: next || undefined },
              { replace: true },
            )
          }
          aria-label="Search all evidence"
          placeholder="Search all evidence"
          icon={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
          wrapperClassName="w-full"
        />
      }
    >
      <ListItem
        selected={selected === 'all'}
        onClick={() => select('all')}
        leading={<List className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />}
        title="All evidence"
        trailing={
          summary.data ? (
            <span className="font-mono text-meta text-ink-500" title="Confirmed evidence">
              {tabCounts(summary.data).findings.toLocaleString()}
            </span>
          ) : null
        }
      />
      {loose.length > 0 || selected === UNTAGGED_AREA ? (
        <ListItem
          selected={selected === UNTAGGED_AREA}
          onClick={() => select(UNTAGGED_AREA)}
          leading={
            <AlertTriangle className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
          }
          title="Not tagged to an area"
          meta={plural(loose.length, 'pain point')}
          trailing={looseProposed > 0 ? <ReviewCount count={looseProposed} /> : null}
        />
      ) : null}
      <ListGroupLabel>Areas</ListGroupLabel>
      {areas.map((a) => (
        <ListItem
          key={a.area_id}
          selected={selected === a.area_id}
          onClick={() => select(a.area_id)}
          leading={<CoverageDot status={a.status} />}
          title={a.label}
          meta={areaStatusLine(a)}
          trailing={a.proposed_count > 0 ? <ReviewCount count={a.proposed_count} /> : null}
        />
      ))}
    </ListPane>
  );

  if (coverage.error) {
    return (
      <ListDetailLayout list={list}>
        <div className="flex flex-col items-center gap-3 p-8">
          <ErrorMessage error={coverage.error} />
          <WorkspaceButton onClick={() => void coverage.refetch()}>Try again</WorkspaceButton>
        </div>
      </ListDetailLayout>
    );
  }
  if (!coverage.data) {
    return (
      <ListDetailLayout list={list}>
        <div className="flex items-center justify-center gap-2 p-8 text-body-sm text-ink-500">
          <Spinner /> Loading findings…
        </div>
      </ListDetailLayout>
    );
  }
  if (selected === 'all') {
    return (
      <ListDetailLayout list={list} fill>
        <EvidenceLedger
          engagementId={engagementId}
          areas={areas}
          status={parseLedgerStatus(search.status)}
          q={q}
          onStatusChange={(status) =>
            onSearchChange(
              { area: 'all', q: search.q, status: status === 'all' ? undefined : status },
              { replace: true },
            )
          }
          onOpenArea={(area, evidence) => onSearchChange({ area, evidence })}
        />
      </ListDetailLayout>
    );
  }
  if (selected === UNTAGGED_AREA) {
    return (
      <ListDetailLayout list={list} detailKey={UNTAGGED_AREA}>
        {untagged.error ? (
          <div className="flex flex-col items-center gap-3 p-8">
            <ErrorMessage error={untagged.error} />
            <WorkspaceButton onClick={() => void untagged.refetch()}>Try again</WorkspaceButton>
          </div>
        ) : !untagged.data ? (
          <div className="flex items-center justify-center gap-2 p-8 text-body-sm text-ink-500">
            <Spinner /> Loading pain points…
          </div>
        ) : (
          <UntaggedPainPoints engagementId={engagementId} areas={areas} painPoints={loose} />
        )}
      </ListDetailLayout>
    );
  }
  return (
    // The area fills the space: its top line stays put and its panes scroll.
    <ListDetailLayout list={list} fill>
      {selected ? (
        <AreaFindingsView
          key={selected}
          engagementId={engagementId}
          areaId={selected}
          areas={areas}
          highlight={search.evidence ?? null}
          view={parseAreaView(search.view)}
          onViewChange={(view) =>
            onSearchChange(
              { area: selected, ...(search.evidence ? { evidence: search.evidence } : {}), view },
              { replace: true },
            )
          }
          nothingYet={
            summary.data
              ? tabCounts(summary.data).findings === 0 && summary.data.kpis.awaiting_review === 0
              : false
          }
        />
      ) : (
        <DetailEmpty title="No areas" text="This framework version has no areas." />
      )}
    </ListDetailLayout>
  );
}
